import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import api from '../../config/api';
import MainHeader from '../../components/MainHeader';
import Footer from '../../components/Footer';
import './CustomListDetailScreen.css';

export default function CustomListDetailScreen() {
  const navigate = useNavigate();
  const { listId } = useParams();
  const location = useLocation();
  const listName = location.state?.listName || 'List';
  const listEmoji = location.state?.listEmoji || '🍽️';

  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [addedIds, setAddedIds] = useState(new Set());
  const [startingSession, setStartingSession] = useState(false);
  const [geoLocation, setGeoLocation] = useState(null);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setGeoLocation({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
        () => {},
        { enableHighAccuracy: true, timeout: 10000 }
      );
    }
  }, []);

  const fetchList = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/custom-lists/${listId}`);
      setRestaurants(data.restaurants || []);
    } catch (err) {
      console.error('Failed to load list:', err);
    } finally {
      setLoading(false);
    }
  }, [listId]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  async function handleSearch(e) {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const { data } = await api.post('/restaurants/search', {
        query: searchQuery.trim(),
        latitude: geoLocation?.latitude,
        longitude: geoLocation?.longitude,
      });
      setSearchResults(data.restaurants || data.results || []);
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setSearching(false);
    }
  }

  async function addToList(restaurant) {
    try {
      await api.post(`/custom-lists/${listId}/restaurants`, { restaurant });
      setAddedIds(prev => new Set([...prev, restaurant.id]));
      fetchList();
    } catch (err) {
      console.error('Failed to add:', err);
    }
  }

  async function removeFromList(restaurantId) {
    setRestaurants(prev => prev.filter(r => r.id !== restaurantId));
    try {
      await api.delete(`/custom-lists/${listId}/restaurants/${restaurantId}`);
    } catch {
      fetchList();
    }
  }

  async function startSession() {
    if (restaurants.length < 2) {
      alert('Add at least 2 restaurants to start a session.');
      return;
    }
    setStartingSession(true);
    try {
      const { data } = await api.post('/sessions/create', {
        mode: 'solo',
        maxParticipants: 1,
        restaurants: restaurants,
        preferences: {},
      });
      navigate(`/session/${data.sessionCode}`, { replace: true });
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to start session');
      setStartingSession(false);
    }
  }

  function openMaps(r) {
    if (r.coordinates?.latitude && r.coordinates?.longitude) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${r.coordinates.latitude},${r.coordinates.longitude}`, '_blank');
    } else if (r.address) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(r.name + ' ' + r.address)}`, '_blank');
    }
  }

  return (
    <div className="main-screen">
      <MainHeader 
        title={`${listEmoji} ${listName}`}
        showBack={true} 
        backPath="/lists"
        rightButton={
          <button className="header-add-btn" onClick={() => setShowSearch(!showSearch)}>
            {showSearch ? '✕' : '+ Add'}
          </button>
        }
      />

      <main className="screen-main">
        <div className="list-detail-container">
          {/* Search Panel */}
          {showSearch && (
            <div className="search-panel">
              <form onSubmit={handleSearch} className="search-form">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search restaurants..."
                  className="search-input"
                  autoFocus
                />
                <button type="submit" className="btn btn-primary" disabled={searching}>
                  {searching ? '...' : '🔍'}
                </button>
              </form>

              {searchResults.length > 0 && (
                <div className="search-results">
                  {searchResults.map(r => (
                    <div key={r.id} className="search-result-item">
                      <div className="search-result-info">
                        <h4>{r.name}</h4>
                        <p>{r.cuisine} • {'$'.repeat(r.priceLevel || 1)}{r.rating > 0 ? ` • ⭐ ${r.rating.toFixed(1)}` : ''}</p>
                      </div>
                      <button
                        className={`add-result-btn ${addedIds.has(r.id) ? 'added' : ''}`}
                        onClick={() => addToList(r)}
                        disabled={addedIds.has(r.id)}
                      >
                        {addedIds.has(r.id) ? '✓' : '+'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Start Session Button */}
          {restaurants.length >= 2 && (
            <button
              className="btn btn-primary btn-lg start-from-list-btn"
              onClick={startSession}
              disabled={startingSession}
            >
              {startingSession ? 'Starting...' : `🔥 Swipe from this list (${restaurants.length})`}
            </button>
          )}

          {/* Restaurant List */}
          {loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Loading...</p>
            </div>
          ) : restaurants.length === 0 ? (
            <div className="empty-state">
              <div className="empty-emoji">📝</div>
              <h2 className="empty-title">Empty list</h2>
              <p className="empty-text">Tap "+ Add" to search and add restaurants.</p>
            </div>
          ) : (
            <div className="list-restaurants">
              {restaurants.map((r, i) => (
                <div key={r.id || i} className="list-restaurant-item">
                  {r.image ? (
                    <div className="lr-image" style={{ backgroundImage: `url(${r.image})` }}></div>
                  ) : (
                    <div className="lr-image lr-placeholder">🍽️</div>
                  )}
                  <div className="lr-info">
                    <h4 className="lr-name">{r.name}</h4>
                    <p className="lr-meta">
                      {r.cuisine} • {'$'.repeat(r.priceLevel || 1)}
                      {r.rating > 0 ? ` • ⭐ ${r.rating.toFixed(1)}` : ''}
                    </p>
                    {r.address && (
                      <p className="lr-address" onClick={() => openMaps(r)}>📍 {r.address}</p>
                    )}
                  </div>
                  <button
                    className="lr-remove-btn"
                    onClick={() => removeFromList(r.id)}
                    title="Remove"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
